import { API_BASE, clearSearchCache } from "../utils";
import { ApiError, requestJson } from "./request";
import { isCollectionQuantity } from "../utils/collection";

/** @typedef {{album_id: number, quantity: number, archive: boolean, updated_at: string}} CollectionStateDto */
/** @typedef {{albums: Object[], page: number, page_size: number, total: number}} ArchivedAlbumsPageDto */
/** @typedef {{albumId: number, action: "local" | "discogs" | "quantity" | "quantityLocal" | "restore" | "restoreLocal", quantity?: number}} CollectionChange */

const isId = (value) => Number.isSafeInteger(value) && value > 0;
const isCollectionState = (data) =>
  isId(data?.album_id) &&
  isCollectionQuantity(data.quantity) &&
  typeof data.archive === "boolean" &&
  typeof data.updated_at === "string" &&
  Number.isFinite(Date.parse(data.updated_at));

/** @param {CollectionChange} change @returns {Promise<CollectionStateDto>} */
export async function changeCollection(change) {
  const { albumId, action, quantity } = change;
  const operations = {
    local: ["collection/", "DELETE"],
    discogs: ["collection/discogs/", "DELETE"],
    quantity: ["quantity/", "PATCH"],
    quantityLocal: ["quantity/local/", "PATCH"],
    restore: ["restore/", "POST"],
    restoreLocal: ["restore/local/", "POST"],
  };
  if (!isId(albumId) || !operations[action])
    throw new Error("Invalid collection operation.");
  const changesQuantity = ["quantity", "quantityLocal"].includes(action);
  if (changesQuantity && !isCollectionQuantity(quantity))
    throw new Error("Quantity must be an integer from 1 to 2147483647.");
  const [path, method] = operations[action];
  const { data, status, requestId } = await requestJson(
    `${API_BASE}/albums/${albumId}/${path}`,
    {
      method,
      ...(changesQuantity
        ? {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ quantity }),
          }
        : {}),
    },
  );
  if (
    status !== 200 ||
    !isCollectionState(data) ||
    data.album_id !== albumId ||
    data.archive !== ["local", "discogs"].includes(action) ||
    (changesQuantity && data.quantity !== quantity) ||
    (["restore", "restoreLocal"].includes(action) && data.quantity !== 1)
  )
    throw new ApiError(
      "Invalid collection response. Retry the original request to recover.",
      status,
      requestId,
    );
  clearSearchCache();
  return data;
}

/** @returns {Promise<ArchivedAlbumsPageDto>} */
export async function listArchivedAlbums({ page = 1, signal } = {}) {
  const query = new URLSearchParams({ page, page_size: 15 });
  const { data } = await requestJson(`${API_BASE}/albums/archived/?${query}`, {
    signal,
  });
  if (
    !Array.isArray(data?.albums) ||
    !isId(data.page) ||
    !isId(data.page_size) ||
    data.page_size > 100 ||
    !Number.isSafeInteger(data.total) ||
    data.total < 0 ||
    !data.albums.every(
      (album) =>
        isCollectionState(album) &&
        album.archive &&
        album.id === album.album_id &&
        typeof album.title === "string",
    )
  )
    throw new Error(
      "Invalid archived releases response. Refresh to try again.",
    );
  return data;
}
